import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Building2, FileText, Home, Loader2, UploadCloud, X } from 'lucide-react';
import {
  calculateDirectCardPrice,
  canCheckoutSketchplanCurrency,
  detectSketchplanCurrency,
  formatMoneyFromMinor,
  persistBillingCurrency,
  sketchplanChargeSummary,
  sketchplanPricingCopy,
  sqFtToApproxSqM,
  sqMToSqFt,
  SKETCHPLAN_COMMERCIAL_RULE_SHORT,
  SKETCHPLAN_PRICING_COPY,
  SKETCHPLAN_QUOTE_SQFT,
  SKETCHPLAN_TURNAROUND,
  type SketchplanCurrency,
  type SketchplanPropertyType,
  type SketchplanSizeBandId,
  type SketchplanTurnaround,
} from '@rehub/pricing';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../constants';
import { supabase } from '../services/supabase';
import { trackSketchplan } from '../lib/analytics';
import CurrencyToggle from './CurrencyToggle';

const DRAFT_KEY = 'sketchplan_guest_draft';
const LOGO_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

type AreaUnit = 'sqft' | 'sqm';

export interface GuestDraft {
  step: 1 | 2 | 3;
  propertyType: SketchplanPropertyType;
  /** Derived from sqFt; kept so the order row and analytics carry the band. */
  sizeBandId: SketchplanSizeBandId;
  /** Approximate size exactly as typed, in `areaUnit`. Missing on pre-2026-10 drafts. */
  sizeInput?: string;
  areaUnit?: AreaUnit;
  sqFt?: number | null;
  floors: number;
  turnaround: SketchplanTurnaround;
  currency: SketchplanCurrency;
  propertyAddress: string;
  notes: string;
  guestName: string;
  email: string;
  phone: string;
  guestSessionId: string;
  sketchUrls: string[];
  logoUrl?: string | null;
  resumeOrderId?: string | null;
}

function newSessionId() {
  return crypto.randomUUID();
}

function loadDraft(): GuestDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) as GuestDraft : null;
  } catch {
    return null;
  }
}

function saveDraft(draft: GuestDraft) {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch { /* ignore */ }
}

function isPdfUrl(url: string) {
  return /\.pdf($|\?)/i.test(url);
}

function FileThumb({
  url,
  label,
  onRemove,
}: {
  url: string;
  label?: string;
  onRemove: () => void;
}) {
  const pdf = isPdfUrl(url);
  return (
    <div className="relative group rounded-xl overflow-hidden border border-slate-600 bg-slate-800 aspect-square">
      {pdf ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-300 px-2">
          <FileText size={28} className="text-teal-400" />
          <span className="text-[10px] text-center leading-tight line-clamp-2">{label || 'PDF'}</span>
        </div>
      ) : (
        <img src={url} alt={label || 'Upload preview'} className="w-full h-full object-cover" />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors"
        aria-label="Remove file"
      >
        <X size={14} />
      </button>
    </div>
  );
}

interface GuestOrderFlowProps {
  onClose: () => void;
  onRequestQuote: (draft: GuestDraft) => void;
  resume?: boolean;
  initialPropertyType?: SketchplanPropertyType | null;
  initialCurrency?: SketchplanCurrency | null;
  customPlanRatePence?: number | null;
}

const GuestOrderFlow: React.FC<GuestOrderFlowProps> = ({
  onClose,
  onRequestQuote,
  resume,
  initialPropertyType,
  initialCurrency,
  customPlanRatePence = null,
}) => {
  const initial = resume ? loadDraft() : null;
  // Older drafts stored only a size band, so send those back to step 1 for a size.
  const [step, setStep] = useState<1 | 2 | 3>(initial?.sizeInput ? initial.step || 1 : 1);
  const [propertyType, setPropertyType] = useState<SketchplanPropertyType>(
    initial?.propertyType || initialPropertyType || 'residential',
  );
  const [sizeInput, setSizeInput] = useState(initial?.sizeInput || '');
  const [areaUnit, setAreaUnit] = useState<AreaUnit>(initial?.areaUnit === 'sqm' ? 'sqm' : 'sqft');
  const [floors, setFloors] = useState(initial?.floors || 1);
  const [turnaround, setTurnaround] = useState<SketchplanTurnaround>(initial?.turnaround || 'standard');
  const [currency, setCurrency] = useState<SketchplanCurrency>(
    () => {
      if (customPlanRatePence != null) return 'GBP';
      return initial?.currency || initialCurrency || detectSketchplanCurrency();
    },
  );
  const [propertyAddress, setPropertyAddress] = useState(initial?.propertyAddress || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [guestName, setGuestName] = useState(initial?.guestName || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [guestSessionId] = useState(initial?.guestSessionId || newSessionId());
  const [sketchUrls, setSketchUrls] = useState<string[]>(initial?.sketchUrls || []);
  const [logoUrl, setLogoUrl] = useState<string | null>(initial?.logoUrl || null);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resumeOrderId = initial?.resumeOrderId || null;

  const sizeValue = Number(sizeInput.replace(/,/g, ''));
  const sqFt = Number.isFinite(sizeValue) && sizeValue > 0
    ? Math.max(1, areaUnit === 'sqm' ? sqMToSqFt(sizeValue) : Math.round(sizeValue))
    : null;

  const quote = useMemo(
    () => calculateDirectCardPrice({
      propertyType,
      sqFt: sqFt ?? undefined,
      turnaround,
      currency,
      customPlanRatePence,
    }),
    [propertyType, sqFt, turnaround, currency, customPlanRatePence],
  );
  const sizeBandId = quote.sizeBand.id;
  const copy = sketchplanPricingCopy(currency);

  const money = (minor: number) => formatMoneyFromMinor(minor, quote.currency);

  const switchUnit = (next: AreaUnit) => {
    if (next === areaUnit) return;
    if (sqFt != null) setSizeInput(String(next === 'sqm' ? sqFtToApproxSqM(sqFt) : sqFt));
    setAreaUnit(next);
  };

  const draftFields = {
    propertyType, sizeBandId, sizeInput, areaUnit, sqFt, floors, turnaround, currency, propertyAddress, notes,
    guestName, email, phone, guestSessionId, sketchUrls, logoUrl,
  };

  useEffect(() => {
    persistBillingCurrency(currency);
  }, [currency]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path !== '/order') return;
    const params = new URLSearchParams(window.location.search);
    params.set('currency', currency);
    if (propertyType) params.set('type', propertyType);
    else params.delete('type');
    const qs = params.toString();
    const next = `${path}?${qs}`;
    const current = `${path}${window.location.search}`;
    if (next !== current) {
      window.history.replaceState({}, '', next);
    }
  }, [currency, propertyType]);

  useEffect(() => {
    trackSketchplan('sketchplan_guest_order_started', { step });
  }, []);

  useEffect(() => {
    saveDraft({ ...draftFields, step, resumeOrderId });
  }, [step, propertyType, sizeInput, areaUnit, floors, turnaround, currency, propertyAddress, notes, guestName, email, phone, guestSessionId, sketchUrls, logoUrl, resumeOrderId]);

  const persistDraft = (nextStep: 1 | 2 | 3) => {
    saveDraft({ ...draftFields, step: nextStep, resumeOrderId });
  };

  const uploadFiles = async (list: FileList | null, purpose: 'sketch' | 'logo') => {
    if (!list?.length) return;
    setError(null);
    const accepted = purpose === 'logo' ? LOGO_ACCEPT : ACCEPTED_IMAGE_TYPES;
    const next = Array.from(list).filter((file) => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) return false;
      if (purpose === 'logo') {
        return accepted.includes(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name);
      }
      return accepted.includes(file.type) || /\.(pdf|jpe?g|png|webp)$/i.test(file.name);
    });
    if (next.length === 0) {
      setError(
        purpose === 'logo'
          ? 'Please upload a JPG, PNG or WebP logo (max 20 MB).'
          : 'Please upload a JPG, PNG, WebP or PDF (max 20 MB).',
      );
      return;
    }

    const batch = purpose === 'logo' ? next.slice(0, 1) : next.slice(0, 8 - sketchUrls.length);
    if (batch.length === 0) {
      setError('You can attach up to 8 sketch files.');
      return;
    }

    if (purpose === 'logo') setUploadingLogo(true);
    else setUploading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('sketchplan-guest-upload', {
        body: {
          guestSessionId,
          purpose,
          files: batch.map((file) => ({
            name: file.name,
            ext: file.name.split('.').pop(),
          })),
        },
      });
      if (fnError || data?.error) throw new Error(data?.error || fnError?.message || 'Upload failed');
      const uploaded: string[] = [];
      for (let i = 0; i < batch.length; i++) {
        const slot = data.uploads[i];
        const put = await fetch(slot.signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': batch[i].type || 'application/octet-stream' },
          body: batch[i],
        });
        if (!put.ok) throw new Error('Could not upload that file. Please try again.');
        uploaded.push(slot.publicUrl);
      }
      if (purpose === 'logo') {
        setLogoUrl(uploaded[0] || null);
      } else {
        setSketchUrls((prev) => [...prev, ...uploaded]);
        trackSketchplan('sketchplan_guest_upload_completed', { file_count: uploaded.length });
      }
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      if (purpose === 'logo') setUploadingLogo(false);
      else setUploading(false);
    }
  };

  const goStep2 = () => {
    if (sqFt == null) {
      setError('Enter the approximate size of the property to see your price.');
      return;
    }
    setError(null);
    if (quote.contactSales) {
      persistDraft(2);
      onRequestQuote({ ...draftFields, step: 2, resumeOrderId });
      return;
    }
    trackSketchplan('sketchplan_guest_service_selected', {
      property_type: propertyType,
      size_band: sizeBandId,
      estimated_sq_ft: sqFt,
      amount_pence: quote.totalPence ?? 0,
    });
    setStep(2);
  };

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (sketchUrls.length === 0) {
      setError('Please upload at least one sketch, photo or PDF.');
      return;
    }
    if (!canCheckoutSketchplanCurrency(currency)) {
      setError('EUR checkout is not enabled for Sketchplan yet. Switch to UK £ or US $ to pay.');
      return;
    }
    setSubmitting(true);
    trackSketchplan('sketchplan_guest_checkout_started', {
      property_type: propertyType,
      size_band: sizeBandId,
      currency,
      amount_pence: quote.totalPence ?? 0,
    });
    try {
      const { data, error: fnError } = await supabase.functions.invoke('sketchplan-guest-checkout', {
        body: {
          propertyType,
          sizeBand: sizeBandId,
          estimatedSqFt: sqFt,
          floors,
          turnaround,
          currency,
          propertyAddress,
          notes,
          guestName,
          guestEmail: email,
          contactPhone: phone,
          guestSessionId,
          sketchUrls,
          logoUrl,
          resumeOrderId,
          brandingNotes: undefined,
        },
      });
      if (fnError || data?.error || !data?.url) {
        throw new Error(data?.error || fnError?.message || 'Checkout failed');
      }
      if (data.orderId) {
        saveDraft({ ...draftFields, step: 3, resumeOrderId: data.orderId });
      }
      window.location.href = data.url;
    } catch (err: any) {
      setError(err?.message || 'Checkout failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[65] bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
            <div>
              <p className="text-xs uppercase tracking-wide text-teal-400 font-semibold">Step {step} of 3</p>
              <h2 className="text-xl font-bold text-white">
                {step === 1 ? 'Select the service' : step === 2 ? 'Upload and details' : 'Contact and payment'}
              </h2>
            </div>
            <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-white" aria-label="Close">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {step === 1 && (
              <>
                <div className="rounded-2xl border border-teal-500/30 bg-gradient-to-br from-teal-500/10 to-cyan-500/5 px-5 py-4">
                  <p className="text-lg font-bold text-white">{copy.headline}</p>
                  <p className="text-sm text-slate-300 mt-1">{copy.supporting}</p>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-slate-300">Billing currency</p>
                  <CurrencyToggle value={currency} onChange={setCurrency} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-300 mb-3">Property type</p>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      { id: 'residential' as const, label: 'Residential', icon: Home, from: `From ${copy.from}` },
                      { id: 'commercial' as const, label: 'Commercial', icon: Building2, from: `From ${copy.commercialFrom} · ${SKETCHPLAN_COMMERCIAL_RULE_SHORT}` },
                    ]).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setPropertyType(opt.id)}
                        aria-pressed={propertyType === opt.id}
                        className={`flex items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-all ${
                          propertyType === opt.id
                            ? 'border-teal-400 bg-teal-500/10 text-white'
                            : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        <opt.icon size={20} />
                        <span>
                          <span className="block">{opt.label}</span>
                          <span className="block text-xs text-slate-400">{opt.from}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <label htmlFor="guest-size" className="text-sm font-medium text-slate-300">Approximate property size</label>
                    <div className="inline-flex rounded-lg border border-slate-700 bg-slate-800/60 p-0.5 text-xs" role="group" aria-label="Area unit">
                      {(['sqft', 'sqm'] as const).map((unit) => (
                        <button
                          key={unit}
                          type="button"
                          onClick={() => switchUnit(unit)}
                          aria-pressed={areaUnit === unit}
                          className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                            areaUnit === unit ? 'bg-teal-500/20 text-teal-300' : 'text-slate-400 hover:text-slate-200'
                          }`}
                        >
                          {unit === 'sqft' ? 'sq ft' : 'm²'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    id="guest-size"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={sizeInput}
                    onChange={(e) => setSizeInput(e.target.value)}
                    placeholder={areaUnit === 'sqft' ? 'e.g. 1650' : 'e.g. 150'}
                    className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-lg font-semibold text-white placeholder-slate-500 focus:outline-none focus:border-teal-400"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">
                    {sqFt != null && areaUnit === 'sqm' && `≈ ${sqFt.toLocaleString('en-GB')} sq ft. `}
                    {sqFt != null && areaUnit === 'sqft' && `≈ ${sqFtToApproxSqM(sqFt).toLocaleString('en-GB')} m². `}
                    {copy.fromSize} {copy.sizeRule}
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-800/60 border border-slate-700 px-4 py-4" aria-live="polite">
                  {sqFt == null ? (
                    <>
                      <p className="text-sm text-slate-400">
                        {propertyType === 'commercial' ? 'Commercial plans from' : 'Residential plans from'}
                      </p>
                      <p className="text-3xl font-bold text-white">
                        {propertyType === 'commercial' ? copy.commercialFrom : copy.from}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Enter the approximate size to see your exact price.</p>
                    </>
                  ) : quote.contactSales ? (
                    <>
                      <p className="text-lg font-semibold text-white">This property needs a custom quote.</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Properties over {SKETCHPLAN_QUOTE_SQFT.toLocaleString('en-GB')} sq ft are quoted individually.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-400">
                        {quote.usedCustomRate ? 'Your account rate' : `Your price · ${quote.sizeBand.label}`}
                      </p>
                      <p className="text-3xl font-bold text-white">{money(quote.totalPence as number)}</p>
                      {(quote.commercialUpliftPence > 0 || quote.expressPence > 0) && quote.residentialPence != null && (
                        <dl className="mt-2 space-y-1 text-xs text-slate-400">
                          <div className="flex justify-between"><dt>Residential price</dt><dd>{money(quote.residentialPence)}</dd></div>
                          {quote.commercialUpliftPence > 0 && (
                            <div className="flex justify-between text-sky-300">
                              <dt>Commercial surcharge ({SKETCHPLAN_COMMERCIAL_RULE_SHORT})</dt>
                              <dd>{money(quote.commercialUpliftPence)}</dd>
                            </div>
                          )}
                          {quote.expressPence > 0 && (
                            <div className="flex justify-between text-amber-300"><dt>Express delivery (+50%)</dt><dd>{money(quote.expressPence)}</dd></div>
                          )}
                        </dl>
                      )}
                      {propertyType === 'commercial' && !quote.usedCustomRate && (
                        <p className="text-xs text-sky-300/90 mt-2">{copy.commercialSurcharge}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-2">{sketchplanChargeSummary(currency)} {SKETCHPLAN_PRICING_COPY.oneProperty} {SKETCHPLAN_PRICING_COPY.amendment}</p>
                      <p className="text-xs text-slate-500 mt-1">{SKETCHPLAN_TURNAROUND.standardLabel}. {SKETCHPLAN_TURNAROUND.expressLabel}. {SKETCHPLAN_TURNAROUND.begins}</p>
                    </>
                  )}
                </div>
                {quote.contactSales && sqFt != null ? (
                  <button type="button" onClick={goStep2} className="w-full py-4 rounded-xl font-semibold bg-amber-500 hover:bg-amber-400 text-slate-900">
                    Request a Quote
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={goStep2}
                    disabled={sqFt == null}
                    className="w-full py-4 rounded-xl font-semibold bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50"
                  >
                    Continue
                  </button>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Upload your sketch, photo, PDF or existing plan</label>
                  <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-600 bg-slate-800/40 px-4 py-10 cursor-pointer hover:border-teal-400">
                    {uploading ? <Loader2 className="text-teal-400 animate-spin" /> : <UploadCloud className="text-teal-400" />}
                    <span className="text-sm text-slate-300">
                      {uploading ? 'Uploading…' : `JPG, PNG, WebP or PDF · up to ${MAX_FILE_SIZE_MB} MB`}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept={ACCEPTED_IMAGE_TYPES.join(',')}
                      multiple
                      disabled={uploading}
                      onChange={(e) => {
                        void uploadFiles(e.target.files, 'sketch');
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {sketchUrls.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-teal-300 mb-2">
                        {sketchUrls.length} file{sketchUrls.length === 1 ? '' : 's'} attached
                      </p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {sketchUrls.map((url, index) => (
                          <FileThumb
                            key={url}
                            url={url}
                            label={`Sketch ${index + 1}`}
                            onRemove={() => setSketchUrls((prev) => prev.filter((item) => item !== url))}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Property address or project reference</label>
                  <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Instructions</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Floors / storeys</label>
                    <select value={floors} onChange={(e) => setFloors(Number(e.target.value))} className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white">
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Turnaround</label>
                    <select value={turnaround} onChange={(e) => setTurnaround(e.target.value as SketchplanTurnaround)} className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white">
                      <option value="standard">{SKETCHPLAN_TURNAROUND.standardOption}</option>
                      <option value="express">{SKETCHPLAN_TURNAROUND.expressOption}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Agency logo (optional)</label>
                  {logoUrl ? (
                    <div className="w-28">
                      <FileThumb url={logoUrl} label="Logo" onRemove={() => setLogoUrl(null)} />
                    </div>
                  ) : (
                    <label className="inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-600 bg-slate-800/40 px-4 py-3 cursor-pointer hover:border-teal-400 text-sm text-slate-300">
                      {uploadingLogo ? <Loader2 size={16} className="animate-spin text-teal-400" /> : <UploadCloud size={16} className="text-teal-400" />}
                      {uploadingLogo ? 'Uploading logo…' : 'Upload logo'}
                      <input
                        type="file"
                        className="hidden"
                        accept={LOGO_ACCEPT.join(',')}
                        disabled={uploadingLogo}
                        onChange={(e) => {
                          void uploadFiles(e.target.files, 'logo');
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                  <p className="mt-1.5 text-xs text-slate-500">JPG, PNG or WebP. Used on the finished floor plan if provided.</p>
                </div>
                <div className="rounded-2xl bg-slate-800/50 border border-slate-700 p-4 text-sm text-slate-300 space-y-2">
                  <p>{SKETCHPLAN_PRICING_COPY.oneProperty}</p>
                  <p>{SKETCHPLAN_PRICING_COPY.amendmentLong}</p>
                  <p>{SKETCHPLAN_PRICING_COPY.output}</p>
                  <p>{SKETCHPLAN_PRICING_COPY.accuracy}</p>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(1)} className="px-4 py-3 rounded-xl border border-slate-600 text-slate-200 inline-flex items-center gap-2">
                    <ArrowLeft size={16} /> Back
                  </button>
                  <button
                    type="button"
                    disabled={uploading || !propertyAddress.trim()}
                    onClick={() => setStep(3)}
                    className="flex-1 py-3 rounded-xl font-semibold bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50"
                  >
                    Continue to payment
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              <form onSubmit={pay} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Your name</label>
                  <input value={guestName} onChange={(e) => setGuestName(e.target.value)} required className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Telephone (optional)</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white" />
                </div>
                <div className="rounded-2xl border border-slate-700 bg-slate-800/40 p-4 text-sm">
                  <p className="font-semibold text-white mb-3">Order summary</p>
                  <dl className="space-y-2 text-slate-300">
                    <div className="flex justify-between"><dt>Property type</dt><dd className="capitalize">{propertyType}</dd></div>
                    <div className="flex justify-between gap-3">
                      <dt>Property size</dt>
                      <dd className="text-right">
                        {sqFt != null ? `${sqFt.toLocaleString('en-GB')} sq ft` : '—'}
                        <span className="block text-xs text-slate-500">{quote.sizeBand.label}</span>
                      </dd>
                    </div>
                    <div className="flex justify-between"><dt>Floors</dt><dd>{floors}</dd></div>
                    <div className="flex justify-between"><dt>Turnaround</dt><dd>{turnaround === 'express' ? SKETCHPLAN_TURNAROUND.expressOption : SKETCHPLAN_TURNAROUND.standardOption}</dd></div>
                    <div className="flex justify-between">
                      <dt>{quote.usedCustomRate ? 'Your account rate' : 'Residential price'}</dt>
                      <dd>{quote.residentialPence != null ? money(quote.residentialPence) : '—'}</dd>
                    </div>
                    {quote.commercialUpliftPence > 0 && (
                      <div className="flex justify-between text-sky-300">
                        <dt>Commercial surcharge ({SKETCHPLAN_COMMERCIAL_RULE_SHORT})</dt>
                        <dd>{money(quote.commercialUpliftPence)}</dd>
                      </div>
                    )}
                    {quote.expressPence > 0 && (
                      <div className="flex justify-between"><dt>Express (+50%)</dt><dd>{money(quote.expressPence)}</dd></div>
                    )}
                    <div className="flex justify-between"><dt>Amendments</dt><dd>One round included</dd></div>
                    <div className="flex justify-between"><dt>Tax</dt><dd>{currency === 'GBP' ? 'Includes VAT' : currency === 'EUR' ? 'Shown as listed' : 'Charged as listed'}</dd></div>
                    <div className="flex justify-between text-white font-semibold pt-2 border-t border-slate-700">
                      <dt>Final amount</dt>
                      <dd>{quote.totalPence != null ? money(quote.totalPence) : '—'}</dd>
                    </div>
                  </dl>
                  <p className="text-xs text-slate-500 mt-3">Billing details are collected securely by Stripe. You will be charged in {currency}.</p>
                </div>
                {error && <p className="text-sm text-red-400">{error}</p>}
                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(2)} className="px-4 py-3 rounded-xl border border-slate-600 text-slate-200">Back</button>
                  <button
                    type="submit"
                    disabled={submitting || quote.totalPence == null || !canCheckoutSketchplanCurrency(currency)}
                    className="flex-1 py-4 rounded-xl font-semibold bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={18} className="animate-spin" />}
                    {canCheckoutSketchplanCurrency(currency)
                      ? `Pay ${quote.totalPence != null ? money(quote.totalPence) : ''} securely`
                      : 'EUR checkout coming soon — switch to £ or $'}
                  </button>
                </div>
              </form>
            )}

            {error && step !== 3 && <p className="text-sm text-red-400">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestOrderFlow;
export { DRAFT_KEY, loadDraft };
